import { INSTRUCTOR_LINK_TAG_KEYS } from "@services/constants/instructorLinkTags";
import { InstructorLinkTagsSetting } from "@services/types/settings/settingsTypes";

type InstructorIds = ReadonlyArray<string | null | undefined>;

/**
 * The instructors of one element of an edition (a session, an occurrence or a
 * slot): the ones saved on it before editing and the ones it ends up with. An
 * element created in the edition has no saved instructors.
 */
export interface InstructorAssignmentPair {
  originalIds: InstructorIds;
  finalIds: InstructorIds;
}

const toIdSet = (ids: InstructorIds): Set<string> =>
  new Set(ids.filter(id => id != null && id !== "").map(String));

const byNumericId = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

/**
 * The instructors unassigned in an edition, as `sessionsCrud` expects them in
 * `unassignedInstructorIds` (CHS-2944).
 *
 * An instructor is unassigned when an element had them saved and no longer
 * has them, whatever control removed or replaced them. The result is the net
 * one for the whole league: an instructor added in the same edition to an
 * element where they were not saved is left out, so moving an instructor
 * between sessions does not report them.
 *
 * Deleted elements must not be passed: deleting a session or reducing its
 * weeks is not an unassignment.
 */
export const netUnassignedInstructorIds = (
  pairs: readonly InstructorAssignmentPair[],
): string[] => {
  const removed = new Set<string>();
  const added = new Set<string>();

  pairs.forEach(pair => {
    const original = toIdSet(pair.originalIds);
    const final = toIdSet(pair.finalIds);

    original.forEach(id => !final.has(id) && removed.add(id));
    final.forEach(id => !original.has(id) && added.add(id));
  });

  return [...removed].filter(id => !added.has(id)).sort(byNumericId);
};

/**
 * Whether the scenario registers group drop dates: the instructor link tags are enabled and
 * include the drop-date tag. It is the condition the backend checks before using
 * `unassignedInstructorIds`, so the editor only sends them when they have an effect.
 */
export const isGroupDropDateEnabled = (
  setting: InstructorLinkTagsSetting | null | undefined,
): boolean =>
  setting?.enabled === true &&
  (setting.tags ?? []).some(tag => tag?.key === INSTRUCTOR_LINK_TAG_KEYS.dropDate);

/**
 * The `sessionsCrud` input key for the given ids, left out when there are none or when the
 * scenario does not register group drop dates.
 */
export const unassignedInstructorsInput = (
  ids: readonly string[],
  isEnabled: boolean,
): { unassignedInstructorIds?: string[] } =>
  isEnabled && ids.length ? { unassignedInstructorIds: [...ids] } : {};
